/**
 * Market-price dashboard (web) — Phase 5, public-ish read.
 *
 * A "stock-exchange" style view of the indicative mango price: a latest-price +
 * %-change hero with a 30-day Sparkline (the same dependency-free pattern as the
 * marketplace MarketStatBar), a grade selector (A|B|C), and the low/high
 * indicative bands per grade.
 *
 * Data (both endpoints are PUBLIC — unauthenticated on the API):
 *   - useMarketPublicControllerGetPrices → indicative low/high/latest micro-ZARP
 *     per grade (the IndicativePriceListDto {data,asOf} envelope). Drives the
 *     low/high bands + the selected-grade latest figure.
 *   - getPriceTimeSeries (src/api/market.ts, the hand-rolled GET
 *     /market/prices/timeseries) → the raw observed points + pctChange over the
 *     window. Drives the hero %-change + the Sparkline. Re-queried per grade.
 *
 * MONEY: every wire figure is micro-ZARP (1e-6 ZARP) carried as a STRING — we
 * convert with microToZarp() for display only. i18n keys live under
 * priceDashboard.* in all four web locales. The nav item is broad (all
 * operational roles) in _app.tsx (central wiring).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MarketStatBar,
  mdSysColor,
  Money,
  SegmentedButton,
  Sparkline,
  Stack,
  Text,
  useColorMode,
} from '@fse/ui';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import { useMarketPublicControllerGetPrices } from '../api/generated/market/market';
import type { IndicativePriceDto } from '../api/generated/models';
import { getPriceTimeSeries, microToZarp, type PriceTimeSeries } from '../api/market';

const PRODUCT = 'mango';
const WINDOW_DAYS = 30;
const GRADES = ['A', 'B', 'C'] as const;
type Grade = (typeof GRADES)[number];

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function PriceDashboardScreen() {
  const { t } = useTranslation();
  const [grade, setGrade] = useState<Grade>('A');

  // Indicative bands per grade (all grades returned; we pick the selected one for
  // the latest figure + low/high bands). The generated public-prices hook.
  const pricesQuery = useMarketPublicControllerGetPrices({ product: PRODUCT });

  // Time series for the selected grade (re-queries on grade change). Soft data —
  // a missing/short series just renders fewer points / no delta.
  const seriesQuery = useQuery({
    queryKey: ['market-price-series', PRODUCT, grade, WINDOW_DAYS],
    queryFn: ({ signal }) =>
      getPriceTimeSeries({ product: PRODUCT, grade, days: WINDOW_DAYS }, signal),
  });

  const prices = pricesQuery.data?.data ?? [];
  const selected: IndicativePriceDto | undefined = prices.find((p) => p.grade === grade);
  const asOf = formatDate(pricesQuery.data?.asOf ?? null);

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('priceDashboard.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {asOf
              ? t('priceDashboard.subtitleAsOf', { date: asOf })
              : t('priceDashboard.subtitle')}
          </Text>
        </Box>
      </Box>

      {/* Grade selector — A|B|C. */}
      <SegmentedButton
        aria-label={t('priceDashboard.gradeLabel')}
        value={grade}
        onChange={(next) => {
          if (typeof next === 'string') setGrade(next as Grade);
        }}
        options={GRADES.map((g) => ({
          value: g,
          label: t('priceDashboard.gradeValue', { grade: g }),
        }))}
      />

      {/* Hero — latest price + %-change + 30-day sparkline for the selected grade. */}
      <PriceHero query={seriesQuery} t={t} />

      {/* Low / high indicative bands (selected grade). */}
      {pricesQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : pricesQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => pricesQuery.refetch()}>
              {t('priceDashboard.retry')}
            </Button>
          }
        >
          {t('priceDashboard.loadError', {
            error:
              pricesQuery.error instanceof FseApiError
                ? pricesQuery.error.message
                : String(pricesQuery.error),
          })}
        </Alert>
      ) : !selected ? (
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('priceDashboard.empty.title')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t('priceDashboard.empty.body', { grade })}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Text variant="title-medium" as="h3">
                {t('priceDashboard.bands.title', { grade })}
              </Text>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                <BandCell
                  label={t('priceDashboard.bands.low')}
                  micro={selected.lowMicro}
                  t={t}
                />
                <BandCell
                  label={t('priceDashboard.bands.latest')}
                  micro={selected.latestMicro}
                  t={t}
                />
                <BandCell
                  label={t('priceDashboard.bands.high')}
                  micro={selected.highMicro}
                  t={t}
                />
              </Box>
              <Divider />
              <Text variant="label-medium" color="text.secondary">
                {t('priceDashboard.bands.note')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

interface BandCellProps {
  label: string;
  micro: string;
  t: TFunction;
}

function BandCell({ label, micro, t }: BandCellProps) {
  return (
    <Box>
      <Text variant="label-medium" color="text.secondary">
        {label}
      </Text>
      <Box sx={{ mt: 0.25 }}>
        <Money amount={microToZarp(micro)} unit={t('priceDashboard.perKg')} size="lg" />
      </Box>
    </Box>
  );
}

interface PriceHeroProps {
  query: UseQueryResult<PriceTimeSeries, unknown>;
  t: TFunction;
}

/**
 * Latest-price + %-change hero with a 30-day Sparkline — the same stock-exchange
 * read as the marketplace MarketStatBar, lifted here as the dashboard centrepiece.
 * Soft-fails: a spinner while pending, an inline note on error / no observations.
 */
function PriceHero({ query, t }: PriceHeroProps) {
  const { mode } = useColorMode();

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  const series = query.data;
  if (query.isError || !series || series.points.length === 0) {
    return (
      <Card>
        <CardContent>
          <Text variant="body-medium" color="text.secondary">
            {t('priceDashboard.hero.noData')}
          </Text>
        </CardContent>
      </Card>
    );
  }

  const points = series.points;
  const latest = microToZarp(points[points.length - 1]!.micro);
  const sparkValues = points.map((p) => microToZarp(p.micro));

  const pct = series.pctChange;
  const direction: 'up' | 'down' | 'neutral' =
    pct == null || Math.abs(pct) < 0.05 ? 'neutral' : pct > 0 ? 'up' : 'down';
  const deltaLabel =
    pct == null
      ? undefined
      : t('priceDashboard.hero.delta', {
          value: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}`,
        });

  const sys = mdSysColor[mode];
  const sparkColor = direction === 'down' ? sys.error : sys.primary;

  return (
    <MarketStatBar
      icon="payments"
      label={t('priceDashboard.hero.label')}
      value={<Money amount={latest} unit={t('priceDashboard.perKg')} size="xl" />}
      delta={deltaLabel}
      deltaDirection={direction}
      meta={t('priceDashboard.hero.window', { days: WINDOW_DAYS })}
      chart={
        <Sparkline
          points={sparkValues}
          width={160}
          height={44}
          color={sparkColor}
          ariaLabel={t('priceDashboard.hero.sparkAria', { days: WINDOW_DAYS })}
        />
      }
    />
  );
}
